import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_URL } from "../components/Constant";

export const crmApi = createApi({
  reducerPath: "crmApi",
  baseQuery: fetchBaseQuery({ baseUrl: `${API_URL}/` }),
  endpoints: (builder) => ({
    getPlayers: builder.query({
      query: () => "crm_players",
    }),
    getPlayerById: builder.query({
      query: (id) => `crm_players/${id}`,
    }),
    getGifts: builder.query({
      query: () => "crm_gifts",
      providesTags: ["Gifts"], // This query will be invalidated when mutations with 'Gifts' tag run
    }),
    getAllGifts: builder.query({
      query: () => "crm_all_gifts", // New endpoint to fetch all gifts
      providesTags: ["Gifts"], // This query will be invalidated when mutations with 'Gifts' tag run
    }),
    executeQuery: builder.mutation({
      query: (sql) => ({
        url: "execute_query", // Ensure correct endpoint
        method: "POST",
        headers: { "Content-Type": "application/json" }, // Explicitly set headers
        body: { sql }, // Ensure body format is correct
      }),
    }),
    submitCallingRequest: builder.mutation({
      query: (requestData) => ({
        url: "submit_calling_request", // Ensure the correct API endpoint
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: requestData, // Send the calling request data
      }),
    }),
    getPendingCallingRequests: builder.query({
      query: () => "pending_calling_requests",
    }),
    getCompletedCallingRequests: builder.query({
      query: () => "completed_calling_requests",
    }),
    submitCallingFeedback: builder.mutation({
      query: ({ calling_requests_id, feedback, status }) => ({
        url: `calling_requests/${calling_requests_id}/feedback`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { feedback, status },
      }),
    }),
    getMissingAddresses: builder.query({
      query: () => "crm_missing_addresses",
    }),

    updateGift: builder.mutation({
      query: (updatedGift) => ({
        url: `crm_gifts/${updatedGift.id}`, // Ensure the correct API endpoint
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: updatedGift,
      }),
      invalidatesTags: ["Gifts"],
    }),
    addGift: builder.mutation({
      query: (newGift) => ({
        url: "crm_gifts",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: newGift, // Save the gift with S3 image URL
      }),
    }),
    uploadGiftImage: builder.mutation({
      query: (formData) => ({
        url: "upload_gift_image",
        method: "POST",
        body: formData,
      }),
    }),
    updateGiftQuantity: builder.mutation({
      query: ({ stock_id, quantity }) => ({
        url: `crm_update_gift_quantity`, // Ensure this endpoint matches your backend
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { stock_id, quantity }, // ✅ Updating with stock_id
      }),
      invalidatesTags: ["Gifts"], // Auto-refetch gifts after update
    }),
    updatePlayerAddress: builder.mutation({
      query: ({ player_id, address }) => ({
        url: `crm_update_address`, // Ensure this endpoint matches your backend
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { player_id, address },
      }),
      invalidatesTags: ["Players"], // Ensures players list updates after mutation
    }),
    reporterLogin: builder.mutation({
      query: (credentials) => ({
        url: "reporter-login", // Backend endpoint for reporter login
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: credentials,
      }),
    }),
  }),
});

export const {
  useGetPlayersQuery,
  useGetPlayerByIdQuery,
  useGetGiftsQuery,
  useExecuteQueryMutation,
  useSubmitCallingRequestMutation,
  useGetPendingCallingRequestsQuery,
  useGetCompletedCallingRequestsQuery,
  useSubmitCallingFeedbackMutation,
  useUpdateGiftMutation,
  useAddGiftMutation,
  useGetAllGiftsQuery,
  useUpdateGiftQuantityMutation,
  useUploadGiftImageMutation,
  useGetMissingAddressesQuery,
  useUpdatePlayerAddressMutation,
} = crmApi;
